import { useState, useEffect } from "react";
import {
  DEFAULT_VERIFY_THRESHOLD,
  DEFAULT_SEPARATION_BRIGHTNESS_THRESHOLD,
  DEFAULT_PRIORITY_AGING_RATE,
} from "@capture/shared";
import type { JobTypeDetail } from "../hooks/useJobTypes";
import { DOCUMENT_CATEGORIES } from "../lib/document-categories";

/** Seed the % input from the JT's stored 0–1 threshold (or the default). */
function verifyPctOf(jobType: JobTypeDetail): number {
  return Math.round(
    (jobType.verificationHighlightThresholds?.threshold ?? DEFAULT_VERIFY_THRESHOLD) * 100,
  );
}

interface GeneralTabProps {
  jobType: JobTypeDetail;
  onSave: (data: Record<string, unknown>) => Promise<void>;
  isSaving: boolean;
  /**
   * F-001 residue (2026-07-08 walk) — General PATCHes the LIVE JobType
   * record on an explicit Save, unlike the Fields/Automations tabs which
   * autosave to a draft blob (F-032's OCC machinery). Autosave semantics
   * are wrong here — every keystroke would mutate production config with
   * no review step. Instead, General reports its dirty state up so the
   * host page can warn before switching away with unsaved changes.
   */
  onDirtyChange?: (dirty: boolean) => void;
}

const MS_PER_HOUR = 3_600_000;

/** WU-1.1 — seed the hours input from the stored ms target ("" = no SLA). */
function slaHoursOf(jobType: JobTypeDetail): string {
  return jobType.slaTargetMs == null ? "" : String(jobType.slaTargetMs / MS_PER_HOUR);
}

export function GeneralTab({ jobType, onSave, isSaving, onDirtyChange }: GeneralTabProps) {
  const [name, setName] = useState(jobType.name);
  // R-132 — Job-type short identifier used as the prefix of structured batch
  // display ids (NOW_20260523_0001). Tolerant of null/empty.
  const [shortName, setShortName] = useState(jobType.shortName ?? "");
  const [description, setDescription] = useState(jobType.description);
  const [category, setCategory] = useState(jobType.documentCategory);
  const [processingMode, setProcessingMode] = useState(jobType.processingMode);
  const [classificationMode, setClassificationMode] = useState(jobType.classificationMode ?? "off");
  // R-266 6a — per-job-type cross-type routing mode for the Capture gate.
  const [crossTypeRouting, setCrossTypeRouting] = useState(jobType.crossTypeRouting ?? "off");
  // R-267 6d — declared-form verify mode (only active when classificationMode === "source_declared").
  const [declaredFormVerify, setDeclaredFormVerify] = useState(jobType.declaredFormVerify ?? "off");
  // F-052 — layout-variants opt-in (Session-136 feature; dormant until enabled here).
  const [allowLayoutVariants, setAllowLayoutVariants] = useState(jobType.allowLayoutVariants ?? false);
  const [splitStrategy, setSplitStrategy] = useState(jobType.splitStrategy ?? "none");
  // R-67 Slice 3a — blank-page separator auto-split config.
  const [sepEnabled, setSepEnabled] = useState(jobType.separationConfig?.enabled ?? false);
  const [sepMode, setSepMode] = useState<"blank" | "barcode">(jobType.separationConfig?.mode ?? "blank");
  const [sepThreshold, setSepThreshold] = useState(jobType.separationConfig?.brightnessThreshold ?? DEFAULT_SEPARATION_BRIGHTNESS_THRESHOLD);
  const [sepKeep, setSepKeep] = useState(jobType.separationConfig?.keepSeparatorPages ?? false);
  // Slice 3b — barcode separation fields.
  const [sepBarcodeValue, setSepBarcodeValue] = useState(jobType.separationConfig?.separatorBarcodeValue ?? "");
  const [sepPatch, setSepPatch] = useState(jobType.separationConfig?.separatorPatchCode ?? false);
  // Slice 3b — duplex/simplex toggle; controls separator sheet-parity.
  const [duplex, setDuplex] = useState(jobType.duplex ?? true);
  // WU-1.1 — SLA target edited as (fractional) hours; blank = no SLA. Stored
  // in ms on the JobType. Aging rate = priority points gained per waiting hour.
  const [slaHours, setSlaHours] = useState(slaHoursOf(jobType));
  const [agingRate, setAgingRate] = useState(
    String(jobType.priorityAgingRate ?? DEFAULT_PRIORITY_AGING_RATE),
  );
  const [affinity, setAffinity] = useState(jobType.documentTypeAffinity);
  const [expectedTypes, setExpectedTypes] = useState(
    (jobType.expectedDocumentTypes ?? []).join(", "),
  );
  const [isActive, setIsActive] = useState(jobType.isActive);
  // R-32 P2: one per-Job-Type verify threshold, edited as a %, drives
  // both tab-skip and the 3-state highlight. Default 0.95 (== the
  // shared DEFAULT_VERIFY_THRESHOLD).
  const [verifyThresholdPct, setVerifyThresholdPct] = useState(verifyPctOf(jobType));
  const [autoClassify, setAutoClassify] = useState(jobType.confidenceThresholds?.autoClassify ?? 0.9);
  const [forceUppercase, setForceUppercase] = useState(jobType.forceUppercase === true);
  // R-155 — tri-state override for the auto-export gate. UI state is the
  // literal string ("inherit" | "manual" | "auto") so the radio group
  // never has to disambiguate `null` vs `undefined`. Mapping back to
  // boolean|null happens in handleSave.
  const initialAutoExport: "inherit" | "manual" | "auto" =
    jobType.requireManualExportRelease == null
      ? "inherit"
      : jobType.requireManualExportRelease
      ? "manual"
      : "auto";
  const [autoExportOverride, setAutoExportOverride] = useState<
    "inherit" | "manual" | "auto"
  >(initialAutoExport);

  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setName(jobType.name);
    setShortName(jobType.shortName ?? "");
    setDescription(jobType.description);
    setCategory(jobType.documentCategory);
    setProcessingMode(jobType.processingMode);
    setClassificationMode(jobType.classificationMode ?? "off");
    setCrossTypeRouting(jobType.crossTypeRouting ?? "off");
    setDeclaredFormVerify(jobType.declaredFormVerify ?? "off");
    setAllowLayoutVariants(jobType.allowLayoutVariants ?? false);
    setSplitStrategy(jobType.splitStrategy ?? "none");
    setSepEnabled(jobType.separationConfig?.enabled ?? false);
    setSepMode(jobType.separationConfig?.mode ?? "blank");
    setSepThreshold(jobType.separationConfig?.brightnessThreshold ?? DEFAULT_SEPARATION_BRIGHTNESS_THRESHOLD);
    setSepKeep(jobType.separationConfig?.keepSeparatorPages ?? false);
    setSepBarcodeValue(jobType.separationConfig?.separatorBarcodeValue ?? "");
    setSepPatch(jobType.separationConfig?.separatorPatchCode ?? false);
    setDuplex(jobType.duplex ?? true);
    setSlaHours(slaHoursOf(jobType));
    setAgingRate(String(jobType.priorityAgingRate ?? DEFAULT_PRIORITY_AGING_RATE));
    setAffinity(jobType.documentTypeAffinity);
    setExpectedTypes((jobType.expectedDocumentTypes ?? []).join(", "));
    setIsActive(jobType.isActive);
    setVerifyThresholdPct(verifyPctOf(jobType));
    setAutoClassify(jobType.confidenceThresholds?.autoClassify ?? 0.9);
    setForceUppercase(jobType.forceUppercase === true);
    setAutoExportOverride(
      jobType.requireManualExportRelease == null
        ? "inherit"
        : jobType.requireManualExportRelease
        ? "manual"
        : "auto",
    );
    setDirty(false);
  }, [jobType]);

  // F-001 residue — surface dirty state to the host page so it can guard
  // tab switches / navigation away from unsaved General edits.
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const markDirty = () => setDirty(true);

  // Slice 3b — barcode mode requires either a value or patch; block Save if neither.
  const barcodeConfigInvalid = sepEnabled && sepMode === "barcode" && !sepBarcodeValue.trim() && !sepPatch;

  const handleSave = async () => {
    const trimmedShort = shortName.trim();
    // WU-1.1 — hours → ms at the save boundary; blank/invalid = null (no SLA).
    const slaHoursNum = parseFloat(slaHours);
    const agingRateNum = parseFloat(agingRate);
    await onSave({
      name: name.trim(),
      // R-132 — null when empty so the allocator falls back to deriving from name.
      shortName: trimmedShort === "" ? null : trimmedShort.toUpperCase(),
      description: description.trim(),
      documentCategory: category,
      processingMode,
      classificationMode,
      crossTypeRouting,
      declaredFormVerify,
      allowLayoutVariants,
      splitStrategy,
      duplex,
      // R-67 Slice 3a/3b — include separator config in every save.
      separationConfig: sepEnabled
        ? sepMode === "barcode"
          ? {
              enabled: true as const,
              mode: "barcode" as const,
              brightnessThreshold: sepThreshold,
              keepSeparatorPages: sepKeep,
              separatorBarcodeValue: sepBarcodeValue.trim() || undefined,
              separatorPatchCode: sepPatch,
            }
          : { enabled: true as const, mode: "blank" as const, brightnessThreshold: sepThreshold, keepSeparatorPages: sepKeep }
        : null,
      documentTypeAffinity: affinity,
      expectedDocumentTypes: expectedTypes
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      isActive,
      forceUppercase,
      slaTargetMs:
        Number.isFinite(slaHoursNum) && slaHoursNum > 0
          ? Math.round(slaHoursNum * MS_PER_HOUR)
          : null,
      priorityAgingRate:
        Number.isFinite(agingRateNum) && agingRateNum >= 0
          ? agingRateNum
          : DEFAULT_PRIORITY_AGING_RATE,
      // R-155 — map tri-state → boolean|null for the PATCH body.
      requireManualExportRelease:
        autoExportOverride === "inherit"
          ? null
          : autoExportOverride === "manual",
      confidenceThresholds: {
        autoClassify,
      },
      verificationHighlightThresholds: {
        threshold: Math.min(100, Math.max(0, verifyThresholdPct)) / 100,
      },
    });
    setDirty(false);
  };

  return (
    <div className="general-tab">
      <div className="gt-section">
        <h3 className="rb-section-title">Identity</h3>
        <div className="gt-field">
          <label className="gt-label">Name</label>
          <input
            className="rb-input"
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); markDirty(); }}
          />
        </div>
        <div className="gt-field">
          <label className="gt-label" htmlFor="gt-short-name">Short Identifier</label>
          <input
            id="gt-short-name"
            className="rb-input"
            type="text"
            value={shortName}
            maxLength={16}
            placeholder="e.g. NOW, DE1545"
            onChange={(e) => {
              // Force uppercase + alphanum-only as the user types so the
              // displayed value matches what the allocator will use.
              const next = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
              setShortName(next);
              markDirty();
            }}
            data-testid="job-type-short-name-input"
          />
          <span className="rb-hint">
            Used as the prefix of batch identifiers (e.g. NOW_20260523_0001).
            Leave blank to auto-derive initials from the name.
          </span>
        </div>
        <div className="gt-field">
          <label className="gt-label">Description</label>
          <textarea
            className="rb-input gt-textarea"
            value={description}
            onChange={(e) => { setDescription(e.target.value); markDirty(); }}
            rows={3}
          />
        </div>
        <div className="gt-field">
          <label className="gt-label">Document Category</label>
          <select
            className="rb-select"
            value={category}
            onChange={(e) => { setCategory(e.target.value); markDirty(); }}
          >
            {DOCUMENT_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c ? c.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) : "-- None --"}
              </option>
            ))}
          </select>
        </div>
        <div className="gt-field">
          <label className="gt-label">Active</label>
          <label className="gt-toggle">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => { setIsActive(e.target.checked); markDirty(); }}
            />
            <span>{isActive ? "Active" : "Inactive"}</span>
          </label>
        </div>
        <div className="gt-field">
          <label className="gt-label">Force Uppercase</label>
          <label className="gt-toggle">
            <input
              type="checkbox"
              checked={forceUppercase}
              onChange={(e) => { setForceUppercase(e.target.checked); markDirty(); }}
            />
            <span>{forceUppercase ? "All fields uppercase" : "Off"}</span>
          </label>
          <span className="rb-hint">When enabled, all keyed values in this job type are auto-capitalized. Can also be set per-field.</span>
        </div>
      </div>

      <div className="gt-section">
        <h3 className="rb-section-title">Processing</h3>
        <div className="gt-field">
          <label className="gt-label">Processing Mode</label>
          <select
            className="rb-select"
            value={processingMode}
            onChange={(e) => { setProcessingMode(e.target.value); markDirty(); }}
          >
            <option value="flow">Flow (documents move independently)</option>
            <option value="batch">Batch (documents move as unit)</option>
            <option value="hybrid">Hybrid (flow + batch reconciliation)</option>
          </select>
        </div>
        {/* R-247 — per-job-type classification mode for the Capture gate. */}
        <div className="gt-field">
          <label className="gt-label" htmlFor="gt-classification-mode">Classification mode</label>
          <select
            id="gt-classification-mode"
            className="rb-select"
            value={classificationMode}
            onChange={(e) => { setClassificationMode(e.target.value); markDirty(); }}
          >
            <option value="off">Off</option>
            <option value="source_declared">Source declared</option>
            <option value="learned">Learned</option>
          </select>
          <span className="rb-hint">
            Off = no gate. Source declared = require a declared form before extraction.
            Learned = fingerprint auto-route with the supervisor correction loop.
          </span>
        </div>
        {/* R-266 6a — per-job-type cross-type routing for intruder documents. */}
        <div className="gt-field">
          <label className="gt-label" htmlFor="gt-cross-type-routing">Cross-type routing</label>
          <select
            id="gt-cross-type-routing"
            className="rb-select"
            value={crossTypeRouting}
            onChange={(e) => { setCrossTypeRouting(e.target.value); markDirty(); }}
          >
            <option value="off">Off (no cross-type detection)</option>
            <option value="route">Route — stop intruders in CS</option>
            <option value="route_auto">Auto-route — route confident intruders automatically</option>
            <option value="reject_batch">Reject batch — a confirmed intruder rejects the whole batch</option>
          </select>
          <span className="rb-hint">
            How to handle documents that fingerprint as a different job type than
            the batch. Off = no detection. Route = hold intruders in Classification
            for an operator. Auto-route = move confident intruders without review.
            Reject batch = a confirmed intruder fails the entire batch.
          </span>
        </div>
        {/* R-267 6d — declared-form verify mode (only shown when classificationMode === "source_declared"). */}
        {classificationMode === "source_declared" && (
          <div className="gt-field">
            <label className="gt-label" htmlFor="gt-declared-form-verify">Declared-form verify</label>
            <select
              id="gt-declared-form-verify"
              className="rb-select"
              value={declaredFormVerify}
              onChange={(e) => { setDeclaredFormVerify(e.target.value); markDirty(); }}
            >
              <option value="off">Off — trust the declaration</option>
              <option value="flag">Flag — stop in classify on a fingerprint disagreement</option>
              <option value="flag_auto">Flag + auto-switch — auto-correct to the fingerprint form</option>
            </select>
            <span className="rb-hint">
              Whether to cross-check a source-declared form against fingerprint classification.
              Off = trust the declaration. Flag = hold mismatches in Classification for an operator.
              Flag + auto-switch = auto-correct confident mismatches to the fingerprint form.
            </span>
          </div>
        )}
        {/* F-052 — layout-variants opt-in. A JobType row field like the rest of
            this tab (live PATCH on Save); every variants surface (Layouts tab,
            operator "New layout of this form" checkbox, rename/merge/retire)
            is dormant until this is on. */}
        <div className="gt-field">
          <label className="gt-label">Form Layout Variants</label>
          <label className="gt-toggle">
            <input
              type="checkbox"
              checked={allowLayoutVariants}
              onChange={(e) => { setAllowLayoutVariants(e.target.checked); markDirty(); }}
              data-testid="job-type-allow-layout-variants"
            />
            <span>{allowLayoutVariants ? "Enabled" : "Off"}</span>
          </label>
          <span className="rb-hint">
            When enabled, operators can declare new physical layouts of this
            job&apos;s forms at classification (&ldquo;New layout of this
            form&rdquo;). Each layout learns OCR separately — same fields,
            rules, and export config — so one form on different letterheads
            stops degrading recognition. Manage declared layouts on the
            form&apos;s Layouts tab.
          </span>
        </div>
        {/* R-252 — per-job-type document-split strategy. */}
        <div className="gt-field">
          <label className="gt-label" htmlFor="gt-split-strategy">Split strategy</label>
          <select
            id="gt-split-strategy"
            className="rb-select"
            value={splitStrategy}
            onChange={(e) => { setSplitStrategy(e.target.value); markDirty(); }}
          >
            <option value="none">None</option>
            <option value="on_occurrence">Split on occurrence</option>
          </select>
          <span className="rb-hint">
            None = one document per import. Split on occurrence = re-segment a page stream into
            documents at each &ldquo;document start&rdquo; page-type (needs per-page identity).
          </span>
        </div>
        {/* R-67 Slice 3a/3b — separator auto-split (blank or barcode). */}
        <div className="gt-field">
          <label className="gt-label">Document Separation</label>
          <label className="gt-toggle">
            <input
              type="checkbox"
              checked={sepEnabled}
              onChange={(e) => { setSepEnabled(e.target.checked); markDirty(); }}
            />
            <span>Auto-split this job&apos;s batches at separator sheets</span>
          </label>
          <label className="gt-toggle" htmlFor="gt-duplex" style={{ marginTop: 4 }}>
            <input
              id="gt-duplex"
              type="checkbox"
              checked={duplex}
              onChange={(e) => { setDuplex(e.target.checked); markDirty(); }}
            />
            <span>Duplex scanning (double-sided)</span>
          </label>
          <span className="rb-hint">Affects separator sheet-parity: in duplex, only a separator on the front (odd page) splits.</span>
          {sepEnabled && (
            <>
              <div className="gt-field" style={{ marginTop: 8 }}>
                <label className="gt-label" htmlFor="gt-sep-mode">Separation method</label>
                <select
                  id="gt-sep-mode"
                  className="rb-select"
                  value={sepMode}
                  onChange={(e) => { setSepMode(e.target.value as "blank" | "barcode"); markDirty(); }}
                >
                  <option value="blank">Blank page</option>
                  <option value="barcode">Barcode / patch code</option>
                </select>
              </div>
              {sepMode === "blank" && (
                <div className="gt-field" style={{ marginTop: 8 }}>
                  <label className="gt-label" htmlFor="gt-sep-threshold">Blank brightness threshold</label>
                  <input
                    id="gt-sep-threshold"
                    className="rb-input"
                    type="number"
                    min={0}
                    max={1}
                    step={0.01}
                    value={sepThreshold}
                    onChange={(e) => { setSepThreshold(Number(e.target.value)); markDirty(); }}
                  />
                  <span className="rb-hint">
                    0–1. A page is blank when its greyscale brightness / 255 exceeds this value.
                    Default: {DEFAULT_SEPARATION_BRIGHTNESS_THRESHOLD}.
                  </span>
                </div>
              )}
              {sepMode === "barcode" && (
                <>
                  <div className="gt-field" style={{ marginTop: 8 }}>
                    <label className="gt-label" htmlFor="gt-sep-barcode-value">Separator barcode value</label>
                    <input
                      id="gt-sep-barcode-value"
                      className="rb-input"
                      type="text"
                      value={sepBarcodeValue}
                      placeholder="e.g. SEPARATOR"
                      onChange={(e) => { setSepBarcodeValue(e.target.value); markDirty(); }}
                    />
                    <span className="rb-hint">
                      A page is a separator when a decoded barcode matches this value (trimmed, case-sensitive).
                    </span>
                  </div>
                  <label className="gt-toggle" style={{ marginTop: 4 }}>
                    <input
                      id="gt-sep-patch"
                      type="checkbox"
                      checked={sepPatch}
                      onChange={(e) => { setSepPatch(e.target.checked); markDirty(); }}
                    />
                    <span>Treat patch codes as separators</span>
                  </label>
                  {barcodeConfigInvalid && (
                    <div className="adapter-config-form__warning" role="alert" style={{ marginTop: 8 }}>
                      Enter a separator barcode value or enable &ldquo;Treat patch codes as separators&rdquo;.
                    </div>
                  )}
                </>
              )}
              <label className="gt-toggle" style={{ marginTop: 4 }}>
                <input
                  type="checkbox"
                  checked={sepKeep}
                  onChange={(e) => { setSepKeep(e.target.checked); markDirty(); }}
                />
                <span>Keep separator pages in the preceding document</span>
              </label>
              {!duplex && (
                <div className="adapter-config-form__warning" role="alert" style={{ marginTop: 8 }}>
                  Simplex separation treats every separator sheet as a boundary and won&apos;t capture
                  notes written on otherwise-blank backs. Use duplex for scanned jobs; simplex
                  suits electronic imports.
                </div>
              )}
            </>
          )}
        </div>
        {/* R-155 — per-job-type override for the auto-export gate. Tri-state
            so admins can either inherit the tenant default or pin this job
            type to a specific mode. */}
        <div className="gt-field">
          <label className="gt-label" htmlFor="gt-auto-export">Hold ready export bundles for supervisor release</label>
          <select
            id="gt-auto-export"
            className="rb-select"
            value={autoExportOverride}
            onChange={(e) => {
              setAutoExportOverride(e.target.value as "inherit" | "manual" | "auto");
              markDirty();
            }}
            data-testid="job-type-auto-export-override"
          >
            <option value="inherit">Inherit tenant default</option>
            <option value="manual">Always hold for supervisor release</option>
            <option value="auto">Never hold (release automatically when ready)</option>
          </select>
          <span className="rb-hint">
            Overrides the tenant-wide hold setting for this job type. When holding,
            accumulated bundles (and batch releases) wait on the Pending Release page
            until a supervisor approves them. Auto-release still requires at least one
            active export destination.
          </span>
        </div>
        <div className="gt-field">
          <label className="gt-label">Document Type Affinity</label>
          <select
            className="rb-select"
            value={affinity}
            onChange={(e) => { setAffinity(e.target.value); markDirty(); }}
          >
            <option value="soft">Soft (prefer same type, allow others)</option>
            <option value="strict">Strict (only same type)</option>
            <option value="round_robin">Round Robin (no preference)</option>
          </select>
        </div>
        <div className="gt-field">
          <label className="gt-label">Expected Document Types</label>
          <input
            className="rb-input"
            type="text"
            value={expectedTypes}
            onChange={(e) => { setExpectedTypes(e.target.value); markDirty(); }}
            placeholder="invoice, credit_memo, statement (comma-separated)"
          />
          <span className="rb-hint">Classification values this job type expects</span>
        </div>
      </div>

      {/* WU-1.1 — the SLA "on" switch. slaTargetMs drives the breach chain
          (priority-aging slaBreached flip, SLA watcher, dashboard/ops badges);
          priorityAgingRate drives queue-order aging for waiting documents. */}
      <div className="gt-section">
        <h3 className="rb-section-title">Service Level</h3>
        <div className="gt-field">
          <label className="gt-label" htmlFor="gt-sla-target-hours">SLA target (hours)</label>
          <input
            id="gt-sla-target-hours"
            className="rb-input"
            type="number"
            min={0}
            step={0.25}
            value={slaHours}
            placeholder="No SLA"
            onChange={(e) => { setSlaHours(e.target.value); markDirty(); }}
            data-testid="job-type-sla-target-hours"
          />
          <span className="rb-hint">
            Documents older than this (from creation) are marked SLA-breached —
            they jump the queue and surface on the dashboard, Monitor breach
            filter, and Ops watchlist. Fractional hours allowed (0.5 = 30 min).
            Leave blank for no SLA.
          </span>
        </div>
        <div className="gt-field">
          <label className="gt-label" htmlFor="gt-priority-aging-rate">Priority aging rate</label>
          <input
            id="gt-priority-aging-rate"
            className="rb-input"
            type="number"
            min={0}
            step={0.1}
            value={agingRate}
            onChange={(e) => { setAgingRate(e.target.value); markDirty(); }}
            data-testid="job-type-priority-aging-rate"
          />
          <span className="rb-hint">
            Priority points a waiting document gains per hour in its current
            step, so older work drifts up the queue. Default: {DEFAULT_PRIORITY_AGING_RATE}.
            Set to 0 to disable aging.
          </span>
        </div>
      </div>

      <div className="gt-section">
        <h3 className="rb-section-title">Confidence Thresholds</h3>
        <div className="gt-field">
          {/* E-058↔E-022 glossary pairing — this is the job-level confidence
              floor (verificationHighlightThresholds.threshold), renamed from
              "Skip & verify above" so it shares vocabulary with the
              Calibration tab and the field drawer's per-field override. */}
          <label className="gt-label" htmlFor="gt-verify-threshold">
            Confidence floor (job default)
          </label>
          <div className="gt-slider-row">
            <input
              id="gt-verify-threshold"
              className="rb-input"
              type="number"
              min={0}
              max={100}
              step={1}
              value={verifyThresholdPct}
              onChange={(e) => {
                setVerifyThresholdPct(e.target.value === "" ? 0 : parseInt(e.target.value, 10));
                markDirty();
              }}
            />
            <span className="gt-slider-value">%</span>
          </div>
        </div>
        <span className="rb-hint">
          Fields at or above this OCR confidence are auto-verified — the cursor
          skips them and they show no highlight. Below it the field stops for
          review (yellow); not OCR-read or a failed rule = red. Set to 100 to
          stop every field. A field can override this floor in the field
          drawer&apos;s Validation section.
        </span>

        <div className="gt-slider-row">
          <label className="gt-label">Auto-Classify</label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={autoClassify}
            onChange={(e) => { setAutoClassify(parseFloat(e.target.value)); markDirty(); }}
          />
          <span className="gt-slider-value">{(autoClassify * 100).toFixed(0)}%</span>
        </div>
        <span className="rb-hint">Documents above this classification confidence skip manual classification</span>
      </div>

      {dirty && (
        <div className="gt-save-bar">
          <button
            className="rb-btn rb-btn-save"
            onClick={handleSave}
            disabled={isSaving || !name.trim() || barcodeConfigInvalid}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      )}
    </div>
  );
}
